import jwt  from 'jsonwebtoken'
import { Subscription } from './interfaces';
import { getPianoPlusOfferId, getPianoVideoTemplateId } from './url';

import { URLSearchParams } from 'url';

// Whether the paid-video paywall this app raised is currently on screen. Lets
// the subscription watcher close it once entitlement arrives, on paths that do
// not run through the in-app-purchase callback (NNBAP-770).
let videoOfferOpen = false

export function isPianoVideoOfferOpen() {
  return videoOfferOpen
}

export function closePianoOffer() {
    videoOfferOpen = false
    let tp = window["tp"] || [];
    if(tp && tp.offer) {
      tp.offer.close()
    }
}

// Exactly one execution per page view: a second one delivers the page's templates again, and a
// template whose container is already taken waits and lands on the next page after navigation.
// Module-scoped because the Tracking component can remount on the same page.
let pianoExecutedForPage: string | null = null
let pianoPendingForPage: string | null = null

function currentPianoPage() {
  return window.location.pathname + window.location.search;
}

export function executePianoExperienceOnce(tp: any) {
  const page = currentPianoPage();
  if(pianoExecutedForPage === page) {
    return;
  }
  if(!(tp && tp.experience && tp.experience.execute)) {
    // Piano not loaded yet: run once it is initialized, if the person is still on this page.
    // Queued on window.tp itself; callers may pass a detached `window.tp || []`.
    if(pianoPendingForPage !== page) {
      pianoPendingForPage = page;
      window["tp"] = window["tp"] || [];
      window["tp"].push(["addHandler", "init", () => {
        pianoPendingForPage = null;
        if(currentPianoPage() === page) {
          executePianoExperienceOnce(window["tp"]);
        }
      }]);
    }
    return;
  }
  pianoExecutedForPage = page;
  try {
    tp.experience.execute();
  }
  catch(e) {
    console.error("** tracking: Error sending tracking data tp.experience.execute;", e);
  }
}

// A change of access on the same page (e.g. logout on a paid article) needs a new evaluation.
export function executePianoExperienceAgain(tp: any) {
  pianoExecutedForPage = null;
  executePianoExperienceOnce(tp);
}

export function executePianoOffer() {
  let tp = window["tp"] || [];
  if(tp && tp.experience) {
    tp.experience.execute();
  }
}

// Shows the paid-video paywall the environment is configured with.
//
// `tp.experience.execute()` cannot address a single experience — it runs every
// experience piano's server considers applicable and piano picks which one
// shows. With the web and the app video experience both live on the same
// `[data-paid-video="true"]` interaction, that arbitration handed the app the
// web offer. Showing the configured offer explicitly is the only way to
// guarantee the app gets the template it is configured for (NNBAP-770).
export function showPianoVideoOffer() {
  let tp = window["tp"] || [];
  const templateId = getPianoVideoTemplateId()
  if(tp && tp.offer && templateId.length > 0) {
    videoOfferOpen = true
    tp.offer.show({
      offerId: getPianoPlusOfferId(),
      templateId: templateId,
      displayMode: "modal",
      showCloseButton: true
    })
  }
}

export async function externalTerm(termId: string) {
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_PIANO_API_URL}/publisher/term/get?term_id=${termId}&api_token=${(process.env.PIANO_API_TOKEN)}`);
    const json = await response.json()
    return json
  } catch (error) {
    return {}
  }
}

export async function term(termId: string) {
  try {      
    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/piano/term/${termId}`, {
      method: 'GET', // *GET, POST, PUT, DELETE, etc.
      // mode: 'cors', // no-cors, *cors, same-origin
      // cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
      // credentials: 'same-origin', // include, *same-origin, omit
      headers: {
        'Content-Type': 'application/json'
        // 'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: 'follow', // manual, *follow, error
      referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    });
    const json = await response.json()
    return json
  } catch (error) {
    return {}
  }
}

export async function externalCheck(resourceId: string, adId: string, userToken: string) {
  try {
    //const response = await fetch(`${process.env.NEXT_PUBLIC_PIANO_API_URL}/access/check?rid=${resourceId}&aid=${(adId)}&user_token=${(userToken)}`);
    //const json = await response.json()
    var decoded = await jwt.decode(userToken)//, process.env.SSO_KEY)
    const response = await fetch(`${process.env.NEXT_PUBLIC_PIANO_API_URL}/publisher/user/access/check?rid=${resourceId}&aid=${(adId)}&uid=${(decoded?.sub)}&api_token=${(process.env.PIANO_API_TOKEN)}`);
    const json = await response.json()
    return json
  } catch (error) {
    return {}
  }
}

export async function check(resourceId: string, adId: string, userToken: string, debugLog?: any) {
  try {
    if(debugLog) {
      debugLog(JSON.stringify({resourceId: resourceId, adId: adId, userToken: userToken}));
    }
      
    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/piano/check`, {
      method: 'POST', // *GET, POST, PUT, DELETE, etc.
      // mode: 'cors', // no-cors, *cors, same-origin
      // cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
      // credentials: 'same-origin', // include, *same-origin, omit
      headers: {
        'Content-Type': 'application/json'
        // 'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: 'follow', // manual, *follow, error
      referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      body: JSON.stringify({resourceId: resourceId, adId: adId, userToken: userToken}) // body data type must match "Content-Type" header
    });
    const json = await response.json()
    return json
  } catch (error) {
    if(debugLog) {
      debugLog("error" + JSON.stringify(error))
    }
    return {}
  }
}

export async function externalSync(termId: string, receiptData: string, userToken: string) {
  var form = new URLSearchParams()
  form.append('term_id', termId)
  form.append('aid', process.env.NEXT_PUBLIC_PIANO_AID || "")
  form.append('user_token', userToken)
  if(receiptData?.length > 0) {
    form.append('check_validity', "true")
    form.append('fields', JSON.stringify({receiptData: receiptData }))
  }
  else {
    form.append('check_validity', "false")
    form.append('fields', JSON.stringify({receiptData: "" }))
  }
  
  
  try {
    // Default options are marked with *
    const response = await fetch(`${process.env.NEXT_PUBLIC_PIANO_API_URL}/conversion/external/create`, {
      method: 'POST', // *GET, POST, PUT, DELETE, etc.
      mode: 'cors', // no-cors, *cors, same-origin
      cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
      credentials: 'same-origin', // include, *same-origin, omit
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: 'follow', // manual, *follow, error
      referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      body: form // body data type must match "Content-Type" header
    })
    
    const json = await response.json()
    // code 0 all ok
    return json
  } catch (error) {    
    return { error: error}
  }
}

export async function sync(subscription: Subscription, userToken: string) {
  try {
    // Default options are marked with *
    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/piano/sync`, {
      method: 'POST', // *GET, POST, PUT, DELETE, etc.
      mode: 'cors', // no-cors, *cors, same-origin
      cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
      credentials: 'same-origin', // include, *same-origin, omit
      headers: {
        'Content-Type': 'application/json'
        // 'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: 'follow', // manual, *follow, error
      referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      body: JSON.stringify({subscription: subscription, userToken: userToken}) // body data type must match "Content-Type" header
    });
    const json = await response.json()
    return json
  } catch (error) {
    return {}
  }
}
